import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { ScrollReveal } from "../hooks/useScrollReveal";
import { toast } from "sonner";
import { Crown, Check, Zap, Shield, Star, FileText, Calendar, X, HelpCircle } from "lucide-react";

const TIER_CONFIG = {
  basic: { icon: Zap, color: "text-muted-foreground bg-muted/70", border: "border-border", gradient: "from-gray-400 to-gray-500", check: "text-muted-foreground/70" },
  standard: { icon: Shield, color: "text-blue-400 bg-blue-500/10", border: "border-blue-500/30", gradient: "from-blue-400 to-indigo-500", check: "text-blue-500" },
  premium: { icon: Crown, color: "text-violet-400 bg-violet-500/10", border: "border-violet-500/30", gradient: "from-violet-400 to-violet-500", check: "text-violet-500" },
  enterprise: { icon: Star, color: "text-amber-400 bg-amber-500/10", border: "border-amber-500/30", gradient: "from-amber-400 to-orange-500", check: "text-amber-500" },
};

// e.g. "Free" for the $0 tier, "$9.99/mo" otherwise.
const priceLabel = (plan) => {
  if (!plan) return "";
  if (plan.price === 0) return plan.price_label || "Free";
  return plan.price_label || `$${plan.price}/mo`;
};

const assistantsLabel = (max) =>
  max === -1 ? "Unlimited assistants" : `Up to ${max} assistant${max === 1 ? "" : "s"}`;

export default function SubscriptionPage() {
  const { user } = useAuth();
  const [pricing, setPricing] = useState([]);
  const [stats, setStats] = useState(null);
  const [currentTier, setCurrentTier] = useState(null);

  const [stripeConfigured, setStripeConfigured] = useState(false);
  const [billingPeriod, setBillingPeriod] = useState("monthly");
  const [subStatus, setSubStatus] = useState(null);

  useEffect(() => {
    api.get("/pricing").then((r) => setPricing(r.data)).catch(() => {});
    api.get("/user/stats").then((r) => { setStats(r.data); setCurrentTier(r.data.current_tier); }).catch(() => {});
    api.get("/payments/config").then((r) => setStripeConfigured(r.data.configured)).catch(() => {});
    api.get("/payments/subscription-status").then((r) => setSubStatus(r.data)).catch(() => {});
    // Handle Stripe redirect
    const params = new URLSearchParams(window.location.search);
    if (params.get("success")) toast.success("Payment successful! Your plan is now active.");
    if (params.get("cancelled")) toast.info("Payment cancelled. No changes were made.");
  }, []);

  const handleSwitch = async (tier) => {
    try {
      const r = await api.post("/payments/create-checkout-session", { tier, billing_period: billingPeriod });
      if (r.data.mocked) {
        setCurrentTier(tier);
        toast.success(r.data.message || `Switched to ${tier} plan (Demo Mode)`);
      } else if (r.data.checkout_url) {
        window.location.href = r.data.checkout_url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to process");
    }
  };

  const handleManageBilling = async () => {
    try {
      const r = await api.post("/payments/customer-portal");
      if (r.data.mocked) {
        toast.info(r.data.message);
      } else if (r.data.portal_url) {
        window.location.href = r.data.portal_url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to open billing portal");
    }
  };

  const currentPlan = pricing.find((p) => p.tier === currentTier) || pricing[0];
  const usagePercent = currentPlan ? Math.min(100, ((stats?.total_messages || 0) / (currentPlan.messages_per_day === -1 ? 1000 : (currentPlan.messages_per_day || 1) * 30)) * 100) : 0;

  return (
    <div className="min-h-screen" data-testid="subscription-page">
      <div className="max-w-5xl mx-auto px-6 py-10">
        <div className="animate-fade-in-up">
          <p className="text-xs uppercase tracking-[0.2em] text-cyan-400 font-semibold mb-1">Subscription</p>
          <div className="flex items-center gap-3">
            <h1 className="font-outfit text-2xl sm:text-3xl font-medium text-foreground" data-testid="subscription-heading">Manage Your Plan</h1>
            {!stripeConfigured && (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-400 font-medium">Demo Mode</span>
            )}
          </div>
          {stripeConfigured && (
            <Button onClick={handleManageBilling} variant="outline" className="mt-2 border-border text-muted-foreground text-xs h-8 px-4 rounded-lg" data-testid="manage-billing-btn">
              Manage Billing
            </Button>
          )}
        </div>

        {/* Current Plan Card */}
        {currentPlan && (
          <ScrollReveal>
            <div className="bg-card border border-border/60 rounded-2xl p-6 mb-10 shadow-sm relative overflow-hidden" data-testid="current-plan-card">
              <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${TIER_CONFIG[currentTier]?.gradient || "from-gray-400 to-gray-500"}`} />
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${TIER_CONFIG[currentTier]?.color || "bg-muted/70 text-muted-foreground"}`}>
                    {(() => { const Icon = TIER_CONFIG[currentTier]?.icon || Zap; return <Icon className="w-6 h-6" />; })()}
                  </div>
                  <div>
                    <h2 className="font-outfit text-lg font-medium text-foreground">{currentPlan.name} Plan</h2>
                    <p className="text-sm text-muted-foreground">{priceLabel(currentPlan)}</p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-outfit font-semibold text-foreground">
                    {stats?.assistant_count || 0}
                    {currentPlan.max_assistants != null && currentPlan.max_assistants !== -1 && (
                      <span className="text-sm text-muted-foreground/70 font-normal"> / {currentPlan.max_assistants}</span>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground">Active Assistants</div>
                </div>
              </div>
              <div className="mt-5 flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Messages this month</span>
                <span className="text-sm font-medium text-foreground">{stats?.total_messages || 0} / {currentPlan.messages_per_day === -1 ? "Unlimited" : currentPlan.messages_per_day * 30}</span>
              </div>
              <div className="w-full bg-muted/70 rounded-full h-2.5 mt-2 overflow-hidden">
                <div
                  className="bg-gradient-to-r from-cyan-400 to-violet-500 h-2.5 rounded-full animate-fill-bar"
                  style={{ width: `${usagePercent}%` }}
                  data-testid="usage-meter"
                />
              </div>
              <div className="flex flex-wrap gap-2 mt-4">
                {currentPlan.features.map((f, i) => (
                  <span key={i} className="flex items-center gap-1 text-xs text-muted-foreground bg-muted/40 px-2.5 py-1 rounded-full">
                    <Check className={`w-3 h-3 ${TIER_CONFIG[currentTier]?.check || "text-cyan-400"}`} /> {f}
                  </span>
                ))}
              </div>
            </div>
          </ScrollReveal>
        )}

        {/* All Plans */}
        <ScrollReveal>
          <h2 className="font-outfit text-xl font-medium text-foreground mb-5">All Plans</h2>
        </ScrollReveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          {pricing.map((p, i) => {
            const isCurrent = p.tier === currentTier;
            const config = TIER_CONFIG[p.tier] || TIER_CONFIG.basic;
            const Icon = config.icon;
            return (
              <ScrollReveal key={p.tier} delay={i * 80}>
                <div className={`bg-card rounded-xl p-5 border-2 transition-all card-hover relative overflow-hidden ${isCurrent ? `${config.border} shadow-md` : "border-border/60 hover:border-border"}`} data-testid={`plan-card-${p.tier}`}>
                  {isCurrent && <div className={`absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r ${config.gradient}`} />}
                  {isCurrent && (
                    <div className="text-[10px] font-semibold uppercase tracking-wider bg-gradient-to-r from-cyan-400 to-violet-500 text-white px-2.5 py-0.5 rounded-full inline-block mb-3">
                      Current
                    </div>
                  )}
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${config.color} mb-3`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <h3 className="font-outfit text-base font-medium text-foreground">{p.name}</h3>
                  <div className="flex items-baseline gap-1 my-2">
                    {p.price === 0 ? (
                      <span className="font-outfit text-2xl font-semibold text-emerald-400">{p.price_label || "Free"}</span>
                    ) : (
                      <>
                        <span className="font-outfit text-2xl font-semibold text-foreground">${p.price}</span>
                        <span className="text-xs text-muted-foreground">/mo</span>
                      </>
                    )}
                  </div>
                  {p.max_assistants != null && (
                    <p className="text-[11px] text-muted-foreground/70 -mt-1 mb-3">{assistantsLabel(p.max_assistants)}</p>
                  )}
                  <ul className="space-y-1.5 mb-5">
                    {p.features.map((f, fi) => (
                      <li key={fi} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <Check className={`w-3 h-3 flex-shrink-0 ${config.check}`} /> {f}
                      </li>
                    ))}
                  </ul>
                  {isCurrent ? (
                    <span className="block text-center text-sm text-cyan-400 font-medium" data-testid={`plan-active-${p.tier}`}>Active Plan</span>
                  ) : (
                    <Button onClick={() => handleSwitch(p.tier)} className="w-full bg-gradient-to-r from-cyan-400 to-violet-500 text-white h-9 text-sm rounded-lg press-down" data-testid={`switch-plan-${p.tier}`}>
                      {stripeConfigured ? `Subscribe to ${p.name}` : `Switch to ${p.name}`}
                    </Button>
                  )}
                </div>
              </ScrollReveal>
            );
          })}
        </div>

        {/* Feature Comparison Matrix */}
        <ScrollReveal>
          <h2 className="font-outfit text-xl font-medium text-foreground mb-5">Feature Comparison</h2>
        </ScrollReveal>
        <ScrollReveal delay={80}>
          <div className="bg-card border border-border/60 rounded-xl overflow-hidden mb-10" data-testid="feature-matrix">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border/60">
                    <th className="text-left px-5 py-3 text-xs font-medium text-muted-foreground w-1/3">Feature</th>
                    {["Basic", "Standard", "Premium", "Enterprise"].map((t) => (
                      <th key={t} className="text-center px-3 py-3 text-xs font-medium text-foreground/80">{t}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[
                    { feature: "AI Assistants", values: ["1", "3", "10", "Unlimited"] },
                    { feature: "Messages per day", values: ["200", "1,000", "5,000", "Unlimited"] },
                    { feature: "All 8 free AI models", values: [true, true, true, true] },
                    { feature: "Voice chat", values: [true, true, true, true] },
                    { feature: "Conversation history", values: ["7 days", "30 days", "Unlimited", "Unlimited"] },
                    { feature: "Custom instructions", values: [false, true, true, true] },
                    { feature: "Priority support", values: [false, false, true, true] },
                    { feature: "API access", values: [false, false, false, true] },
                  ].map((row, ri) => (
                    <tr key={ri} className={`${ri > 0 ? "border-t border-border/40" : ""} hover:bg-white/5 transition-colors`}>
                      <td className="px-5 py-3 text-foreground/80 text-xs">{row.feature}</td>
                      {row.values.map((v, vi) => (
                        <td key={vi} className="text-center px-3 py-3">
                          {v === true ? <Check className="w-4 h-4 text-emerald-500 mx-auto" /> :
                           v === false ? <X className="w-4 h-4 text-muted-foreground/60 mx-auto" /> :
                           <span className="text-xs text-foreground/80 font-medium">{v}</span>}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </ScrollReveal>

        {/* Need Help Choosing */}
        <ScrollReveal>
          <div className="bg-gradient-to-br from-cyan-500/10 to-violet-500/10 border border-cyan-500/20 rounded-2xl p-6 mb-10 flex items-start gap-4" data-testid="help-choosing">
            <div className="w-10 h-10 rounded-xl bg-cyan-500/15 flex items-center justify-center flex-shrink-0">
              <HelpCircle className="w-5 h-5 text-cyan-400" />
            </div>
            <div>
              <h3 className="font-outfit text-base font-medium text-foreground mb-1">Need help choosing?</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Every plan includes all 8 free AI models — tiers only differ in usage limits.
                <strong> Basic</strong> is free forever and perfect for getting started. <strong>Standard</strong> adds more messages and assistant slots.
                <strong> Premium</strong> raises the limits further and unlocks the web widget — best for power users.
                <strong> Enterprise</strong> is for teams needing unlimited everything with API access.
              </p>
            </div>
          </div>
        </ScrollReveal>

        {/* Subscription Status */}
        <ScrollReveal>
          <h2 className="font-outfit text-xl font-medium text-foreground mb-5">Subscription Status</h2>
        </ScrollReveal>
        <ScrollReveal delay={100}>
          <div className="bg-card border border-border/60 rounded-xl overflow-hidden" data-testid="subscription-status-panel">
            <div className="flex items-center justify-between px-5 py-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-muted/40 flex items-center justify-center">
                  <FileText className="w-4 h-4 text-muted-foreground/70" />
                </div>
                <div>
                  <p className="text-sm text-foreground font-medium">
                    {(subStatus?.tier || currentTier || "free").replace(/^./, (c) => c.toUpperCase())} Plan
                  </p>
                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {subStatus?.current_period_end ? `Renews ${new Date(subStatus.current_period_end).toLocaleDateString()}` : "No active billing cycle"}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-[10px] uppercase tracking-wider font-medium text-muted-foreground">{subStatus?.status || "free"}</p>
                {!stripeConfigured && (
                  <p className="text-[10px] text-amber-400 font-medium">Demo mode</p>
                )}
              </div>
            </div>
            <div className="px-5 py-3 border-t border-border/40 bg-white/[0.03] text-xs text-muted-foreground">
              Detailed invoice history will appear here once Stripe billing is configured.
            </div>
          </div>
        </ScrollReveal>
      </div>

    </div>
  );
}
